import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import SelectFromQuery from '~/prix/react/components/form/inputs/selectFromQuery'
import AppLayout from '~/components/appLayout'
import theme, { colors, primaryColors, secondaryTones } from '~/design'
import useGoogleMaps from '~/prix/react/hooks/googleMaps'
import useConfig from '~/prix/react/hooks/config'
import { useNavigate, useParams } from 'react-router-dom'
import useItems from '~/prix/react/hooks/items'
import AlertIcon from '~/components/icons/ui/16px_alert.svg'
import ZoomIcon from '~/components/icons/ui/16px_zoom-2.svg'
import EditIcon from '~/components/icons/ui/16px_edit-72.svg'
import useAPI from '~/prix/react/hooks/api'
import DateField from '~/prix/react/components/form/inputs/dateTimeField'
import { format, subYears, startOfYear } from 'date-fns'
import LogoSebraeApp from '~/meta/sebraeLogoApp'
import AgentReportContent from './agentReportContent'
import { toastInfo } from '~/components/toast'
import { useAttendantVisitLocations, VisitLocation } from './hooks/useAttendantVisitLocations'
import AgentInformationQuery from './queries/agentInformation.query'
import AgentFetchSelfQuery from './queries/agentFetchSelf.query'
import AttendantListQuery from './queries/attendantList.query'
import { PulseLoader } from 'react-spinners'
import { useEvent, useMedia } from 'react-use'
import { ascending, entity, query } from '~/prix'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 24px 24px;
  gap: 12px;
  flex: 1;

  @media (max-width: 1100px) {
    padding: 12px 16px 16px;
  }
`

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  width: 100%;
  padding: 26px 20px;
`

const Banner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 16px 24px 0;

  h1 {
    color: ${colors.sebraeBlueDarker};
    padding: 0;
    margin: 0;

    @media (max-width: 768px) {
      display: none;
    }
  }

  @media (max-width: 768px) {
    justify-content: center;
  }
`

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px 24px 12px;
  padding: 24px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: ${theme.shadow(0.75)};
  z-index: 2;

  @media (max-width: 1100px) {
    margin: 16px;
    padding: 16px;
  }
`

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;

  &:first-of-type {
    @media (max-width: 768px) {
      flex-direction: column;
    }
  }
`

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

const LabelSelect = styled.label`
  display: block;
  margin-bottom: 8px;
`

const FilterItemCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
`

const UfSelect = styled.select`
  width: 100%;
  height: 40px;
  border: 1px solid #cccccc;
  border-radius: 4px;
  padding: 0 12px;
  background-color: #fff;
  color: #333333;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: #2684ff;
  }

  &:disabled {
    background-color: #f1f1f1;
    color: #838383;
    cursor: not-allowed;
  }
`

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.darkGrey};
`

const MessageIcon = styled.div`
  margin-bottom: 8px;
`

const HeaderAgentSelected = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  position: sticky;
  top: 0px;
  z-index: 1;
  padding: 12px 24px;
  background-color: #fafafa;

  @media (max-width: 1100px) {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 6px;
    padding: 6px 16px;
  }
`

const FilterItemSelected = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: ${props => (props.$color ? props.$color : theme.colors.mediumGrey)};
  color: ${props => (props.$color ? '#fff' : theme.colors.darkGrey)};
  border-radius: 16px;
  margin: 0;
  transition: opacity 0.2s ease-in-out;
  cursor: pointer;
  line-height: 1.25;
  opacity: 1;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: 768px) {
    padding: 4px 10px;
    font-size: 12px;
  }
`

function validateDateRange(startDateStr: string, endDateStr: string): boolean {
  const [sy, sm, sd] = startDateStr.split('-').map(Number)
  const [ey, em, ed] = endDateStr.split('-').map(Number)
  const start = new Date(sy, (sm || 1) - 1, sd || 1)
  const end = new Date(ey, (em || 1) - 1, ed || 1)
  start.setHours(12, 0, 0, 0)
  end.setHours(12, 0, 0, 0)
  return start <= end
}

function formatDateString(dateStr: string): string {
  const [year, month, day] = dateStr.split('-')
  return `${day}/${month}/${year}`
}

function getDefaultDateRange() {
  return {
    startDate: format(startOfYear(new Date()), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  }
}

const DATA_SOURCE_ID = 6

export default function AgentReportScreen() {
  const { context } = useAPI()
  const { google } = useGoogleMaps()
  const config = useConfig()
  const apiKey = config.services.googleConfig.apiKey

  const [attendantSelected, setAttendantSelected] = useState<{
    id: string
    name: string
    hash: string
  }>()
  const [attendantComparisonSelected, setAttendantComparisonSelected] = useState<{
    id: string
    name: string
    hash: string
  }>()
  const [startDate, setStartDate] = useState<string | null>(() => getDefaultDateRange().startDate)
  const [endDate, setEndDate] = useState<string | null>(() => getDefaultDateRange().endDate)

  const [isComparisonEnabled, setIsComparisonEnabled] = useState(false)
  const [selectedUfs, setSelectedUfs] = useState<string[]>([])
  const [currentKey, setCurrentKey] = useState(0)

  const navigate = useNavigate()
  const { agentHash: paramsAgentHash } = useParams()

  const inputRefs = useRef<{
    attendant: HTMLDivElement | null
    attendantComparison: HTMLDivElement | null
    startDate: HTMLDivElement | null
    endDate: HTMLDivElement | null
    uf: HTMLDivElement | null
  }>({
    attendant: null,
    attendantComparison: null,
    startDate: null,
    endDate: null,
    uf: null,
  })

  const isMobile = useMedia('max-width: 1100px')
  useEvent('resize', () => {
    setCurrentKey(prev => prev + 1)
  })

  const handleFocusInput = useCallback(
    (type: 'attendant' | 'attendantComparison' | 'startDate' | 'endDate' | 'uf') => {
      const element = inputRefs.current[type]

      if (element) {
        const input = element.querySelector('input, select') as HTMLInputElement | HTMLSelectElement | null
        if (input) {
          input.focus()
        }
      }
    },
    [],
  )

  const agentInformations = useItems(
    () =>
      paramsAgentHash !== undefined ? AgentInformationQuery(paramsAgentHash) : (null as never),
    [paramsAgentHash],
    {
      cache: 60 * 60 * 24 * 7,
      autoLoad: paramsAgentHash !== undefined && !!context.user,
    },
  )

  const { validAttendances, isLoading: isLoadingAttendances } = useAttendantVisitLocations({
    agent: attendantSelected || { id: '', name: '' },
    startDate: startDate || '',
    endDate: endDate || '',
    autoLoad: !!context.user,
  })

  const {
    validAttendances: validAttendancesComparison,
    isLoading: isLoadingAttendancesComparison,
  } = useAttendantVisitLocations({
    agent: attendantComparisonSelected || { id: '', name: '' },
    startDate: startDate || '',
    endDate: endDate || '',
    autoLoad: !!context.user && isComparisonEnabled && !!attendantComparisonSelected,
  })

  const attendantListQueryFactory = useCallback(
    (input: string) => AttendantListQuery(input, context),
    [context],
  )

  const stateList = useItems(
    () =>
      query('state')
        .select({
          id: entity('state').property('id'),
          name: entity('state').property('name'),
          abbreviation: entity('state').property('abbreviation'),
        })
        .order(ascending('name')),
    [],
    {
      cache: 60 * 60 * 24 * 7,
      autoLoad: true,
    },
  )

  const allUfOptions = useMemo(() => {
    if (!stateList.items) return []

    return stateList.items
      .map((item: any) => String(item.abbreviation || '').trim().toUpperCase())
      .filter((item: string) => item !== '')
      .sort((first: string, second: string) => first.localeCompare(second))
  }, [stateList.items])

  const ufNameMap = useMemo(() => {
    if (!stateList.items) return {}

    return stateList.items.reduce((acc: Record<string, string>, item: any) => {
      const uf = String(item.abbreviation || '').trim().toUpperCase()
      if (uf) {
        acc[uf] = item.name || ''
      }
      return acc
    }, {})
  }, [stateList.items])

  const allUfsSelected = useMemo(() => {
    if (allUfOptions.length === 0) return false

    return selectedUfs.length === 0
  }, [allUfOptions, selectedUfs])

  const selectedUfDropdownValue = useMemo(() => {
    if (allUfsSelected || selectedUfs.length !== 1) {
      return 'ALL'
    }

    return selectedUfs[0]
  }, [allUfsSelected, selectedUfs])

  const handleUfDropdownChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value

      if (value === 'ALL') {
        setSelectedUfs([])
        return
      }

      setSelectedUfs(value ? [value] : [])
    },
    [],
  )

  const resetAgentDependentFilters = useCallback(() => {
    const defaultDateRange = getDefaultDateRange()

    setSelectedUfs([])
    setStartDate(defaultDateRange.startDate)
    setEndDate(defaultDateRange.endDate)
  }, [])

  const handleAttendant = useCallback(
    (
      type: 'attendant' | 'attendantComparison',
      valueId: unknown,
      valueName: unknown,
      hash?: unknown,
    ) => {
      if (type === 'attendant' && typeof valueId === 'string' && typeof valueName === 'string') {
        if (attendantComparisonSelected?.id === valueId) {
          toastInfo('O agente principal deve ser diferente do agente de comparação.')
          return
        }

        setAttendantSelected({
          id: valueId,
          name: valueName,
          hash: typeof hash === 'string' ? hash : '',
        })
        navigate(`/app/agent-report/${hash}/${DATA_SOURCE_ID}`)
      } else if (type === 'attendant' && !valueId) {
        setAttendantSelected(undefined)
        setAttendantComparisonSelected(undefined)
        setIsComparisonEnabled(false)
        resetAgentDependentFilters()

        navigate('/app/agent-report')
      } else if (
        type === 'attendantComparison' &&
        typeof valueId === 'string' &&
        typeof valueName === 'string'
      ) {
        if (attendantSelected?.id === valueId) {
          toastInfo('O agente para comparação deve ser diferente do agente principal.')
          return
        }

        setAttendantComparisonSelected({
          id: valueId,
          name: valueName,
          hash: typeof hash === 'string' ? hash : '',
        })

        if (isMobile) {
          setCurrentKey(prev => prev + 1)
        }
      } else if (type === 'attendantComparison' && !valueId) {
        setAttendantComparisonSelected(undefined)
      }
    },
    [navigate, attendantSelected, attendantComparisonSelected, isMobile, resetAgentDependentFilters],
  )

  useEffect(() => {
    if (attendantSelected) return

    if (agentInformations.isLoading === false && paramsAgentHash !== undefined) {
      if (agentInformations.items && agentInformations.items?.length > 0) {
        const valueId = agentInformations.items[0].agentCpf
        const valueName = agentInformations.items[0].name
        const valueHash = agentInformations.items[0].hash

        setAttendantSelected({
          id: String(valueId),
          name: String(valueName),
          hash: String(valueHash),
        })
      } else if (agentInformations.query === null) {
        setAttendantSelected(undefined)
      }
    }
  }, [
    agentInformations.items,
    agentInformations.isLoading,
    agentInformations.query,
    attendantSelected,
    paramsAgentHash,
  ])

  const fetchSelf = useItems(
    () =>
      context && context.user?.roles.includes('agent')
        ? AgentFetchSelfQuery(context.user.cpf ?? '')
        : (null as never),
    [context.user?.roles],
    {
      cache: 60 * 60 * 24 * 7,
      autoLoad: !!context.user && context.user.roles.includes('agent') ? true : false,
    },
  )

  useEffect(() => {
    if (
      context.user?.roles.includes('agent') &&
      fetchSelf.isLoading === false &&
      fetchSelf.items &&
      fetchSelf.items?.length > 0
    ) {
      handleAttendant(
        'attendant',
        fetchSelf.items[0].agentCpf,
        fetchSelf.items[0].name,
        fetchSelf.items[0].hash,
      )
    }
  }, [fetchSelf.isLoading, fetchSelf.items])

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      date?.setHours(0, 0, 0, 0)
      const formattedDate = date ? format(date, 'yyyy-MM-dd') : null
      if (formattedDate && endDate && !validateDateRange(formattedDate, endDate)) {
        toastInfo('A data inicial não pode ser maior que a final.')
        return
      }
      setStartDate(formattedDate)
    },
    [endDate],
  )

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      date?.setHours(23, 59, 59, 999)
      const formattedDate = date ? format(date, 'yyyy-MM-dd') : null
      if (formattedDate && startDate && !validateDateRange(startDate, formattedDate)) {
        toastInfo('A data inicial não pode ser maior que a final.')
        return
      }
      setEndDate(formattedDate)
    },
    [startDate],
  )

  const handleComparisonToggle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsComparisonEnabled(e.target.checked)
    if (!e.target.checked) {
      setAttendantComparisonSelected(undefined)
    }
  }, [])

  const filterAttendancesBySelectedUfs = useCallback(
    (attendances: VisitLocation[]) => {
      if (selectedUfs.length === 0) return attendances

      const selectedUfsSet = new Set(selectedUfs.map(uf => uf.toUpperCase()))

      return attendances.filter(attendance => {
        const attendanceState = String(attendance.legalEntityState || '')
          .trim()
          .toUpperCase()

        return selectedUfsSet.has(attendanceState)
      })
    },
    [selectedUfs],
  )

  const filteredMainAttendances = useMemo(
    () => filterAttendancesBySelectedUfs(validAttendances),
    [validAttendances, filterAttendancesBySelectedUfs],
  )

  const filteredComparisonAttendances = useMemo(
    () => filterAttendancesBySelectedUfs(validAttendancesComparison),
    [validAttendancesComparison, filterAttendancesBySelectedUfs],
  )

  const validAttendancesProp = useMemo(
    () =>
      isComparisonEnabled && attendantComparisonSelected
        ? [filteredMainAttendances, filteredComparisonAttendances]
        : [filteredMainAttendances],
    [
      isComparisonEnabled,
      attendantComparisonSelected,
      filteredMainAttendances,
      filteredComparisonAttendances,
    ],
  )

  const agentProp = useMemo(
    () =>
      isComparisonEnabled && attendantComparisonSelected && attendantSelected
        ? [attendantSelected, attendantComparisonSelected]
        : attendantSelected
          ? attendantSelected
          : [],
    [isComparisonEnabled, attendantSelected, attendantComparisonSelected],
  )

  const dateRangeProp = useMemo(() => ({ startDate, endDate }), [startDate, endDate])

  const isLoadingAttendancesProp = useMemo(
    () =>
      isComparisonEnabled && attendantComparisonSelected
        ? [isLoadingAttendances, isLoadingAttendancesComparison]
        : isLoadingAttendances,
    [
      isComparisonEnabled,
      attendantComparisonSelected,
      isLoadingAttendances,
      isLoadingAttendancesComparison,
    ],
  )

  return (
    <AppLayout title='Relatório interativo de agente' dockActive='studies-and-links'>
      <Banner>
        <h1>Relatório interativo de agente</h1>
        <LogoSebraeApp
          width={200}
          checkColor={colors.sebraeBlueDarker}
        />
      </Banner>
      <FilterWrapper>
        <FilterRow>
          <FilterItem ref={el => (inputRefs.current.attendant = el)}>
            <LabelSelect>Agente</LabelSelect>
            <SelectFromQuery
              placeholder='Buscar...'
              idProperty='id'
              isSearchInput={true}
              debounceMs={500}
              preLoad={false}
              labelProperty='name'
              isDisabled={context.user?.roles.includes('agent') ? true : false}
              queryFactory={attendantListQueryFactory}
              value={
                attendantSelected?.id
                  ? { id: attendantSelected.id, name: attendantSelected.name }
                  : undefined
              }
              onDirectChange={value =>
                handleAttendant('attendant', value?.agentCpf, value?.name, value?.hash)
              }
            />
          </FilterItem>
          {isComparisonEnabled && (
            <FilterItem ref={el => (inputRefs.current.attendantComparison = el)}>
              <LabelSelect>Agente para comparar</LabelSelect>
              <SelectFromQuery
                placeholder='Buscar...'
                idProperty='id'
                isSearchInput={true}
                debounceMs={500}
                preLoad={false}
                labelProperty='name'
                isDisabled={false}
                queryFactory={attendantListQueryFactory}
                value={
                  attendantComparisonSelected?.id
                    ? {
                        id: attendantComparisonSelected.id,
                        name: attendantComparisonSelected.name,
                      }
                    : undefined
                }
                onDirectChange={value =>
                  handleAttendant('attendantComparison', value?.agentCpf, value?.name, value?.hash)
                }
              />
            </FilterItem>
          )}
        </FilterRow>
        <FilterRow>
          <FilterItem ref={el => (inputRefs.current.uf = el)}>
            <LabelSelect>Estado (UF)</LabelSelect>
            <UfSelect value={selectedUfDropdownValue} onChange={handleUfDropdownChange}>
              <option value='ALL'>Todas os estados</option>
              {allUfOptions.map(uf => (
                <option key={uf} value={uf}>
                  {ufNameMap[uf]} - {uf}
                </option>
              ))}
            </UfSelect>
          </FilterItem>
        </FilterRow>
        <FilterRow>
          <div ref={el => (inputRefs.current.startDate = el)} style={{ flex: 1 }}>
            <DateField
              key={`start-${attendantSelected?.id}`}
              id={`start-date`}
              value={startDate}
              label='Data de início'
              onDirectChange={handleStartDateChange}
              placeholder='Início'
              meta={{
                maxDate: new Date(),
                minDate: subYears(new Date(), 3),
              }}
              isRequired
            />
          </div>
          <div ref={el => (inputRefs.current.endDate = el)} style={{ flex: 1 }}>
            <DateField
              key={`end-${attendantSelected?.id}`}
              id={`end-date`}
              value={endDate || null}
              label='Data final'
              onDirectChange={handleEndDateChange}
              placeholder='Final'
              meta={{
                maxDate: new Date(),
                minDate: subYears(new Date(), 3),
              }}
              isRequired
            />
          </div>
        </FilterRow>
        <FilterRow>
          <FilterItemCheckbox htmlFor='comparison-checkbox'>
            <input
              type='checkbox'
              id='comparison-checkbox'
              checked={isComparisonEnabled}
              onChange={handleComparisonToggle}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            Comparar com outro agente
          </FilterItemCheckbox>
        </FilterRow>
      </FilterWrapper>
      {attendantSelected && startDate && endDate && (
        <HeaderAgentSelected>
          Filtros ativos:
          <FilterItemSelected
            $color={primaryColors.default}
            onClick={() => handleFocusInput('attendant')}
          >
            {attendantSelected.name}

            <EditIcon fill='#fff' width={13} height={13} />
          </FilterItemSelected>
          {isComparisonEnabled && attendantComparisonSelected && (
            <FilterItemSelected
              $color={secondaryTones[1]}
              onClick={() => handleFocusInput('attendantComparison')}
            >
              {attendantComparisonSelected.name}

              <EditIcon fill='#fff' width={13} height={13} />
            </FilterItemSelected>
          )}
          {startDate && (
            <FilterItemSelected onClick={() => handleFocusInput('startDate')}>
              De: {formatDateString(startDate)}
              <EditIcon fill={colors.darkGray} width={13} height={13} />
            </FilterItemSelected>
          )}
          {endDate && (
            <FilterItemSelected onClick={() => handleFocusInput('endDate')}>
              Até: {formatDateString(endDate)}
              <EditIcon fill={colors.darkGray} width={13} height={13} />
            </FilterItemSelected>
          )}
          <FilterItemSelected onClick={() => handleFocusInput('uf')}>
            UF: {allUfsSelected ? 'Todas' : selectedUfs[0] || 'Todas'}
            <EditIcon fill={colors.darkGray} width={13} height={13} />
          </FilterItemSelected>
        </HeaderAgentSelected>
      )}
      <Wrapper>
        {attendantSelected && startDate && endDate ? (
          <AgentReportContent
            key={currentKey}
            agent={agentProp}
            dateRange={dateRangeProp}
            google={google}
            apiKey={apiKey}
            selectedUfs={selectedUfs}
            validAttendances={validAttendancesProp}
            isLoadingAttendances={isLoadingAttendancesProp}
          />
        ) : !startDate || !endDate ? (
          <ComponentWrapper>
            <MessageWrapper>
              <MessageIcon>
                <AlertIcon fill={theme.colors.mediumGrey} width={48} height={48} />
              </MessageIcon>
              <p>Selecione uma data de início e uma data final para visualizar as informações.</p>
            </MessageWrapper>
          </ComponentWrapper>
        ) : agentInformations.isLoading ||
          isLoadingAttendancesProp ||
          (!!paramsAgentHash && !attendantSelected) ? (
          <ComponentWrapper>
            <MessageWrapper>
              <MessageIcon>
                <PulseLoader color={theme.colors.mediumGrey} size={10} />
              </MessageIcon>
              <p>Carregando...</p>
            </MessageWrapper>
          </ComponentWrapper>
        ) : (
          <ComponentWrapper>
            {context.user?.roles.includes('agent') &&
            fetchSelf.isLoading === false &&
            fetchSelf.items &&
            fetchSelf.items?.length === 0 ? (
              <MessageWrapper>
                <MessageIcon>
                  <AlertIcon fill={theme.colors.mediumGrey} width={48} height={48} />
                </MessageIcon>
                <p>Não foi possível localizar dados do agente.</p>
              </MessageWrapper>
            ) : (
              <MessageWrapper>
                <MessageIcon>
                  <ZoomIcon fill={theme.colors.mediumGrey} width={48} height={48} />
                </MessageIcon>
                <p>Escolha um agente para visualizar as informações.</p>
              </MessageWrapper>
            )}
          </ComponentWrapper>
        )}
      </Wrapper>
    </AppLayout>
  )
}
